<script setup lang="ts">
import { Subscribe } from '~/entities/subscribe';
import ArticleParser from '~/entities/article/ui/parser/ArticleParser.vue';
import Pagination from '~/shared/ui/pagination/Pagination.vue';
import TagAiMaterialCard from './TagAiMaterialCard.vue';
import {
  CLIENT_API_TAG_DETAIL_KEY,
  CLIENT_API_TAG_DETAIL_URL,
  CLIENT_API_TAG_MATERIALS_KEY,
  CLIENT_EXPERT_URL,
  CLIENT_TAG_AI_URL,
} from '~/shared/api/client/news/news.const';
import type { ClientApiNewsPaginatedResponse } from '~/shared/api/client/news/news.dto';
import type {
  AITagDTO,
  AITagMaterialResultDTO,
} from '~/shared/api/client/tags/ai-tags.dto';
import { AI_TAG_MATERIALS_ON_PAGE } from '../lib/const';
import { SEO_TITLE } from '~/shared/lib/seo';
import { useCustomFetch } from '~/shared/lib/useCustomFetch';
import { useOrigin } from '~/shared/lib/useOrigin';
import { declineWords } from '~/shared/utils/declineWords';

const route = useRoute();
const router = useRouter();

const tagId = computed(() => Number(route.params.id));

if (!tagId.value) {
  throw createError({
    status: 404,
    message: 'Тег не найден',
    fatal: true,
  });
}

const { data, error } = await useCustomFetch<AITagDTO>(
  () => `${CLIENT_API_TAG_DETAIL_URL}${tagId.value}/`,
  {
    method: 'GET',
    key: CLIENT_API_TAG_DETAIL_KEY,
  },
);

if (error.value || !data.value) {
  throw createError({
    status: 404,
    message: 'Тег не найден',
    fatal: true,
  });
}

const tag = computed(() => data.value);

const title = computed(() => tag.value?.display_name || tag.value?.name || '');

const bodyBlocks = computed(() =>
  tag.value?.show_body ? (tag.value?.body?.blocks ?? []) : [],
);

const pageFromQuery = computed(() => {
  const page = Number(route.query.page);
  return Number.isFinite(page) && page > 0 ? Math.floor(page) : 1;
});

const pageNumber = useState(
  'client-tag-materials-page',
  () => pageFromQuery.value,
);

if (pageNumber.value !== pageFromQuery.value) {
  pageNumber.value = pageFromQuery.value;
}

const { data: materials, status: materialsStatus } = await useCustomFetch<
  ClientApiNewsPaginatedResponse<AITagMaterialResultDTO>
>(() => `${CLIENT_API_TAG_DETAIL_URL}${tagId.value}/materials/`, {
  method: 'GET',
  key: CLIENT_API_TAG_MATERIALS_KEY,
  watch: [pageNumber],
  params: {
    page: pageNumber,
    page_size: AI_TAG_MATERIALS_ON_PAGE,
  },
});

const materialsCount = computed(
  () => materials.value?.count ?? tag.value?.materials_count ?? 0,
);

const buildItemLink = (item: AITagMaterialResultDTO) => {
  if (item.type === 'expert') {
    return `${CLIENT_EXPERT_URL}${item.data.id}`;
  }
  return item.data.redirect || `/${item.data.type}/${item.data.id}`;
};

if (tag.value) {
  const origin = useOrigin();
  const url = `${origin}${CLIENT_TAG_AI_URL}${tagId.value}`;

  const metaTitle = tag.value.meta_title || title.value;
  const ogTitle = tag.value.og_title || metaTitle;
  const description = tag.value.meta_description || '';
  const ogDescription = tag.value.og_description || description;

  useHead({
    title: SEO_TITLE.replace('%%TITLE%%', metaTitle),
    link: [{ rel: 'canonical', href: url }],
    meta: [
      { name: 'description', content: description },
      { property: 'og:type', content: 'website' },
      { property: 'og:url', content: url },
      { property: 'og:title', content: ogTitle },
      { property: 'og:description', content: ogDescription },
    ],
  });
}

watch(
  () => pageFromQuery.value,
  (value) => {
    if (pageNumber.value !== value) pageNumber.value = value;
  },
);

watch(
  () => pageNumber.value,
  (value) => {
    if (!import.meta.client) return;

    const nextQuery = { ...route.query };

    if (value <= 1) {
      delete nextQuery.page;
    } else {
      nextQuery.page = String(value);
    }

    router.push({ path: route.path, query: nextQuery });

    nextTick(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  },
);
</script>

<template>
  <div :class="$style.tag__page">
    <div :class="$style.tag__header">
      <div :class="$style.tag__header__top">
        <ButtonBack />
        <div v-if="materialsCount" :class="$style.tag__header__results">
          {{ materialsCount }}
          {{
            declineWords(materialsCount, [
              'Результат',
              'Результата',
              'Результатов',
            ])
          }}
        </div>
      </div>

      <div :class="$style.tag__badge">
        <Icon :class="$style.tag__badge__icon" name="my-icon:i-ai" />
        <span>Создано с помощью CRE AI</span>
      </div>

      <div :class="$style.tag__disclaimer">
        Результат предназначен для информационной поддержки и не заменяет
        экспертную оценку.
      </div>

      <h1 :class="$style.tag__title">{{ title }}</h1>
    </div>

    <div v-if="bodyBlocks.length" :class="$style.tag__body">
      <ArticleParser :blocks="bodyBlocks" />
    </div>
    <div :class="$style.tag__bodyDivider" />

    <div :class="$style.tag__wrapper">
      <div
        v-if="materials?.results && materials.results.length > 0"
        :class="$style.tag__materialsTitle"
      >
        В материалах CRE {{ title }} упоминается в:
      </div>

      <Loader v-if="materialsStatus === 'pending'" />

      <TagAiMaterialCard
        v-for="(item, index) in materials?.results"
        v-else-if="materials?.results && materials.results.length > 0"
        :key="`tag-material-${index}`"
        :title="item.data.title"
        :category="
          item.type === 'material' ? item.data.category?.name || false : false
        "
        :category-link="
          item.type === 'material' && item.data.category
            ? `/category/${item.data.category.id}`
            : false
        "
        :date="item.data.publication_date"
        :subject="item.data.tag_summary ?? undefined"
        :link="buildItemLink(item)"
      />

      <div
        v-else-if="materials?.results && materials.results.length === 0"
        :class="$style.noMaterials"
      >
        <div :class="$style.noMaterials__title">Материалов по тегу нет</div>
      </div>

      <Pagination
        v-if="materialsStatus === 'success'"
        :class="$style.tag__pagination"
        theme="client"
        page-name="client-tag-materials-page"
        :total="materials?.count || 0"
        :sibling-count="1"
        show-edges
        :items-per-page="AI_TAG_MATERIALS_ON_PAGE"
        :default-page="pageNumber"
        @page-change="pageNumber = $event"
      />
      <div v-else-if="materialsStatus === 'error'" :class="$style.noMaterials">
        <div :class="$style.noMaterials__title">
          Не удалось получить материалы
        </div>
      </div>
    </div>

    <Subscribe :class="$style.subscribe" />
  </div>
</template>

<style module lang="scss">
.tag__page {
  display: flex;
  flex-direction: column;
  flex: 1;
}

.tag__header {
  padding: var(--space-2-5x);

  &__top {
    display: flex;
    justify-content: space-between;
    margin-bottom: var(--space-2-5x);
  }

  &__results {
    @include text_caption1_caps;
    color: var(--color-gray-text);
    text-transform: uppercase;
  }
}

.tag__badge {
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  gap: var(--space-1x);
  @include text_caption1_medium;
  color: var(--color-red-2);
  margin-bottom: var(--space-1x);

  &__icon {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
    color: var(--color-red-2);
  }
}

.tag__disclaimer {
  @include text_caption1_medium;
  color: var(--color-gray-text);
  margin-bottom: var(--space-2x);
}

.tag__title {
  font-weight: 600;
  font-size: 40px;
  line-height: 110%;
  color: var(--color-black-1);
  margin: 0;
}

.tag__body {
  padding: 0 var(--space-2-5x) var(--space-2-5x);
  @include text_body1;
  color: var(--color-black-1);

  :global(p) {
    margin-top: 0;
    margin-bottom: var(--space-2x);
  }

  :global(p:first-child) {
    @include text_article_body1_medium;
  }

  :global(p:last-child) {
    margin-bottom: 0;
  }
}

.tag__bodyDivider {
  border-bottom: 1px solid var(--color-gray-line);
}

.tag__materialsTitle {
  @include text_h4;
  color: var(--color-black-1);
  padding: var(--space-2-5x);
  padding-bottom: 0;
}

.tag__wrapper {
  height: 100%;
  flex: 1;
}

.tag__pagination {
  border-bottom: 1px solid var(--color-gray-line);
}

.noMaterials {
  padding: var(--space-2-5x);

  &__title {
    @include text_h5;
  }
}

@media (max-width: 767px) {
  .tag__materialsTitle {
    @include text_h5;
  }
}

@media (max-width: 479px) {
  .tag__header {
    padding: 16px;
  }

  .tag__title {
    @include text_h4;
  }

  .tag__body {
    padding-left: 16px;
    padding-right: 16px;
  }

  .tag__materialsTitle {
    padding: var(--space-2x);
    padding-bottom: 0;
  }

  .noMaterials {
    padding: var(--space-2x);
  }
}
</style>
